import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import useAxios from "../../utils/useAxios";
import { FaSpinner } from "react-icons/fa";
import { hideModal, showModal } from "../../utils/modalUtility";
import Pagination from "../common/pagination";

const INITIAL_FORM_DATA = {
  employeeId: null,
  propertyId: null,
  taskTitle: "",
  taskDescription: "",
  enquiryIds: [],
  dueDateTime: "",
  priority: "",
};

const getFormDataFromTask = (task) => ({
  employeeId: task.employee_id || task.assigned_employee_id || null,
  propertyId: task.property_id || null,
  taskTitle: task.task_title || "",
  taskDescription: task.task_description || "",
  enquiryIds: Array.isArray(task.enquiry_ids)
    ? task.enquiry_ids.map(Number)
    : typeof task.enquiry_ids === "string" && task.enquiry_ids.trim()
      ? task.enquiry_ids.split(",").map(Number)
      : [],
  dueDateTime: task.due_date_time ? task.due_date_time.slice(0, 16) : "",
  priority: task.priority || "",
});

const TaskManagement = () => {
  const axiosAPI = useAxios();

  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [properties, setProperties] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchDropdownData = async () => {
    try {
      setLoadingOptions(true);

      const [employeesRes, propertiesRes, enquiriesRes, tasksRes] =
        await Promise.all([
          axiosAPI.get("/api/users/get-all-users"),
          axiosAPI.get("/api/properties/get-properties"),
          axiosAPI.get("/api/enquiries/get-enquiries?page=1"),
        ]);

      setEmployees(Array.isArray(employeesRes?.data) ? employeesRes.data : []);
      setProperties(
        Array.isArray(propertiesRes?.data) ? propertiesRes.data : [],
      );
      setEnquiries(
        Array.isArray(enquiriesRes?.data?.results)
          ? enquiriesRes.data.results
          : [],
      );
    } catch (error) {
      console.error("Error fetching task form options:", error);
      window.notifyMessage(
        "Error",
        "Unable to load task form options.",
        "danger",
      );
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const employeeOptions = useMemo(
    () =>
      employees.map((item) => ({
        value: item.id,
        label: item.name || item.full_name || `Employee #${item.id}`,
      })),
    [employees],
  );

  const propertyOptions = useMemo(
    () =>
      properties.map((item) => {
        const name = item.property_name || item.name || `Property #${item.id}`;
        const lat = item.latitude || item.lat;
        const lng = item.longitude || item.lng || item.long;
        const gps = lat && lng ? ` | GPS: ${lat}, ${lng}` : "";

        return {
          value: item.id,
          label: `${name}${gps}`,
        };
      }),
    [properties],
  );

  const enquiryOptions = useMemo(
    () =>
      enquiries.map((item) => {
        const name = item.full_name || item.name || "Lead";
        const phone =
          item.phone_number || item.phone || item.mobile || item.contact_number;
        const property = item.property_name ? ` | ${item.property_name}` : "";

        return {
          value: item.id,
          label: `${name}${phone ? ` (${phone})` : ""}${property}`,
        };
      }),
    [enquiries],
  );

  const selectedEmployee =
    employeeOptions.find((opt) => opt.value === formData.employeeId) || null;

  const selectedProperty =
    propertyOptions.find((opt) => opt.value === formData.propertyId) || null;

  const selectedEnquiries = enquiryOptions.filter((opt) =>
    formData.enquiryIds.includes(opt.value),
  );

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const closeModal = () => {
    setFormData(INITIAL_FORM_DATA);
    setIsEditMode(false);
    setEditingTaskIndex(null);
  };

  const openEditModal = (task, index) => {
    setFormData(getFormDataFromTask(task));
    setIsEditMode(true);
    setEditingTaskIndex(index);
    showModal("newTaskModal");
  };

  const openAddModal = () => {
    closeModal();
    showModal("newTaskModal");
  };

  const handleDeleteTask = async (index) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this task?",
    );

    if (!confirmed) return;

    const taskId = tasks[index]?.id;
    try {
      await axiosAPI.delete(`/api/task-management/delete-task/${taskId}`);
      window.notifyMessage("Success", "Task deleted successfully.", "success");
      await fetchAllTask();
    } catch (error) {
      console.error("Error deleting task:", error);
      window.notifyMessage(
        "Error",
        error?.response?.data?.message || "Failed to delete task.",
        "danger",
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.employeeId) {
      window.notifyMessage("Error", "Please select an employee.", "danger");
      return;
    }

    if (!formData.propertyId) {
      window.notifyMessage("Error", "Please select a property.", "danger");
      return;
    }

    if (!formData.taskTitle.trim()) {
      window.notifyMessage("Error", "Task title is required.", "danger");
      return;
    }

    if (!formData.dueDateTime) {
      window.notifyMessage("Error", "Due date and time is required.", "danger");
      return;
    }

    if (!formData.priority) {
      window.notifyMessage("Error", "Please select priority.", "danger");
      return;
    }

    const payload = {
      assigned_employee_id: formData.employeeId,
      property_id: formData.propertyId,
      task_title: formData.taskTitle,
      task_description: formData.taskDescription,
      enquiry_ids: formData.enquiryIds,
      due_date_time: formData.dueDateTime,
      priority: formData.priority,
    };

    // console.log("Payload", payload);

    // return;

    try {
      setSubmitting(true);

      if (isEditMode && editingTaskIndex !== null) {
        const taskId = tasks[editingTaskIndex]?.id;
        await axiosAPI.put(
          `/api/task-management/update-task/${taskId}`,
          payload,
        );
        window.notifyMessage(
          "Success",
          "Task updated successfully.",
          "success",
        );
      } else {
        await axiosAPI.post("/api/task-management/add-task", payload);
        window.notifyMessage(
          "Success",
          "Task created successfully.",
          "success",
        );
      }

      await fetchAllTask();
      setFormData(INITIAL_FORM_DATA);
      setIsEditMode(false);
      setEditingTaskIndex(null);
      hideModal("newTaskModal");
    } catch (error) {
      console.error("Error creating task:", error);
      window.notifyMessage(
        "Error",
        error?.response?.data?.message || "Failed to create task.",
        "danger",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fetchAllTask = async () => {
    try {
      const tasksRes = await axiosAPI.get("/api/task-management");
      const data = tasksRes?.data?.data;
      setTasks(Array.isArray(data) ? data : []);
      setTotalPages(tasksRes?.data?.totalPages);
    } catch (error) {
      console.log("Error fetching all tasks:", error);
    }
  };

  useEffect(() => {
    fetchAllTask();
  }, []);

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-end flex-column flex-md-row">
          <div>
            <button
              type="button"
              className="btn btn-dark btn-round mb-4"
              onClick={openAddModal}
            >
              Add Task
            </button>
          </div>
        </div>

        <div
          className="modal fade"
          id="newTaskModal"
          tabIndex="-1"
          aria-labelledby="newTaskModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newTaskModalLabel">
                  {isEditMode ? "Edit Task" : "Add Task"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                {loadingOptions ? (
                  <p className="mb-0">Loading form options...</p>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Assign to Employee</label>
                        <Select
                          options={employeeOptions}
                          value={selectedEmployee}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              employeeId: selected?.value || null,
                            }))
                          }
                          placeholder="Select one employee"
                          isClearable
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Property (linked with map & GPS)
                        </label>
                        <Select
                          options={propertyOptions}
                          value={selectedProperty}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              propertyId: selected?.value || null,
                            }))
                          }
                          placeholder="Select one property"
                          isClearable
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="taskTitle" className="form-label">
                          Task Title
                        </label>
                        <input
                          id="taskTitle"
                          type="text"
                          className="form-control"
                          placeholder="Enter task title"
                          value={formData.taskTitle}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="dueDateTime" className="form-label">
                          Due Date & Time
                        </label>
                        <input
                          id="dueDateTime"
                          type="datetime-local"
                          className="form-control"
                          value={formData.dueDateTime}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-8 mb-3">
                        <label htmlFor="taskDescription" className="form-label">
                          Task Description
                        </label>
                        <textarea
                          id="taskDescription"
                          className="form-control"
                          rows="4"
                          placeholder="Enter task description"
                          value={formData.taskDescription}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-4 mb-3">
                        <label htmlFor="priority" className="form-label">
                          Priority (Low / Medium / High)
                        </label>
                        <select
                          id="priority"
                          className="form-select"
                          value={formData.priority}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Select Priority --</option>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <label className="form-label">
                          Call List (list of leads or numbers)
                        </label>
                        <Select
                          isMulti
                          options={enquiryOptions}
                          value={selectedEnquiries}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              enquiryIds: (selected || []).map(
                                (item) => item.value,
                              ),
                            }))
                          }
                          placeholder="Select multiple enquiries"
                          closeMenuOnSelect={false}
                        />
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        data-bs-dismiss="modal"
                        onClick={closeModal}
                      >
                        Close
                      </button>

                      <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <FaSpinner className="spinner" spin />
                        ) : isEditMode ? (
                          "Update Task"
                        ) : (
                          "Create Task"
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4 className="card-title">All Tasks ({tasks.length})</h4>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Task Title</th>
                    <th>Assigned Employee</th>
                    <th>Property</th>
                    <th>Priority</th>
                    <th>Due Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No Data Found
                      </td>
                    </tr>
                  ) : (
                    tasks.map((task, index) => {
                      const employee =
                        employeeOptions.find(
                          (item) =>
                            item.value ===
                            (task.employee_id ?? task.assigned_employee_id),
                        )?.label || "-";
                      const property =
                        propertyOptions.find(
                          (item) => item.value === task.property_id,
                        )?.label || "-";

                      return (
                        <tr key={`${task.task_title}-${index}`}>
                          <td>{task.task_title || "-"}</td>
                          <td>{employee}</td>
                          <td>{property}</td>
                          <td className="text-capitalize">
                            {task.priority || "-"}
                          </td>
                          {/* due_date_time in utc */}
                          <td>
                            {new Date(task.due_date_time)?.toLocaleString()}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                onClick={() => openEditModal(task, index)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                onClick={() => handleDeleteTask(index)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <Pagination totalPages={totalPages} setPage={setPage} page={page} />
      </div>
    </div>
  );
};

export default TaskManagement;
